import { useState, useEffect } from "react";
import { motion, useMotionValue, useTransform, animate } from "framer-motion";

/**
 * AnimatedCounter - Smoothly animates numbers from one value to another
 */
const AnimatedCounter = ({ from, to, isAnimating }) => {
  const count = useMotionValue(from);
  const rounded = useTransform(count, (latest) => Math.round(latest));
  const [displayValue, setDisplayValue] = useState(from);

  // Reset counter when props change
  useEffect(() => {
    count.set(from);
    setDisplayValue(from);
  }, [from, to, count]);

  // Subscribe to rounded value changes
  useEffect(() => {
    const unsubscribe = rounded.onChange(setDisplayValue);
    return unsubscribe;
  }, [rounded]);

  // Start animation when triggered
  useEffect(() => {
    if (isAnimating) {
      console.log(`Starting number animation: ${from} → ${to}`);
      const controls = animate(count, to, {
        duration: 1.0,
        ease: "easeOut",
      });
      return controls.stop;
    }
  }, [isAnimating, count, to, from]);

  return <span>{displayValue}</span>;
};

/**
 * AnimatedPointsBox - A box that shows previous points height, then animates to current points
 * 
 * Animation sequence:
 * 1. Appears instantly at previous points height
 * 2. Waits 1 second for user to see previous state
 * 3. Animates smoothly to current points height over 1.5 seconds
 * 4. Numbers count up/down during animation
 */
const AnimatedPointsBox = ({
  previousPoints,
  currentPoints,
  title,
  bgColor = "bg-blue-100",
  textColorTitle = "text-pink400",
  textColorPoints = "text-pink200",
  maxPoints = 40,
  minHeight = 60,
  maxHeight = 240,
}) => {
  const [startAnimation, setStartAnimation] = useState(false);
  // Calculate box heights based on points (linear mapping)
  const calculateHeight = (points) => {
    const heightRange = maxHeight - minHeight;
    return minHeight + (points / maxPoints) * heightRange;
  };

  const previousHeight = calculateHeight(previousPoints);
  const currentHeight = calculateHeight(currentPoints);

  // Animation lifecycle
  useEffect(() => {
    setStartAnimation(false);
    console.log(`Animation starting: ${previousPoints} → ${currentPoints}`);

    // Start animation after 0.5 second delay
    const timer = setTimeout(() => {
      setStartAnimation(true);
    }, 500);

    return () => clearTimeout(timer);
  }, [previousPoints, currentPoints]);

  return (
    <div className="flex flex-col items-center">
      {/* Animated Points Display above the box */}
      <div className={`text-[40px] font-bold ${textColorPoints} mb-1`}>
        <AnimatedCounter
          from={previousPoints}
          to={currentPoints}
          isAnimating={startAnimation}
        />
      </div>

      {/* Animated Points Box */}
      <motion.div
        key={`${previousPoints}-${currentPoints}`}
        className={`relative flex flex-col items-center justify-start rounded-[15px] w-[350px] overflow-hidden ${title === "PLAYERS" ? "" : bgColor}`}
        style={{
          height: previousHeight,
          background: title === "PLAYERS"
            ? 'linear-gradient(to right, #dbeafe 0%, #dbeafe 50%, #fce7f3 50%, #fce7f3 100%)'
            : undefined
        }}
        animate={{ height: startAnimation ? currentHeight : previousHeight }}
        transition={{
          duration: 0.5,
          ease: "easeOut",
        }}
      >
      </motion.div>

      {/* Title below the box */}
      <h4 className={`text-[22px] font-bold ${textColorTitle} mt-[16px]`}>
        {title}
      </h4>
    </div>
  );
};

export default AnimatedPointsBox;
